// @flow strict

import * as React from "react";
import { Frame, InFrame } from "~robin-hood/Frame";
import { Clock } from "~robin-hood/Clock";
import { Toolbar } from "~robin-hood/registered/Toolbar";
import { WaitForStart } from "~robin-hood/registered/WaitForStart";
import { PruebaInicial } from "~robin-hood/registered/pruebas/PruebaInicial";
import { PruebaConsola } from "~robin-hood/registered/pruebas/PruebaConsola";
import { PruebaDigitos } from "~robin-hood/registered/pruebas/PruebaDigitos";
import { PruebaAhorcado } from "~robin-hood/registered/pruebas/PruebaAhorcado";
import { PruebaPoker } from "~robin-hood/registered/pruebas/PruebaPoker";
import { PruebaCasillas } from "~robin-hood/registered/pruebas/PruebaCasillas";
import { PruebaColores } from "~robin-hood/registered/pruebas/PruebaColores";
import { PruebaSonidos } from "~robin-hood/registered/pruebas/PruebaSonidos";
import { PruebaFinal } from "~robin-hood/registered/pruebas/PruebaFinal";
import { SeleccionAgentes } from "~robin-hood/registered/SeleccionAgentes";
import { Header } from "~common/components/Header";
import { EquipoInfo } from "~robin-hood/registered/EquipoInfo";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from "react-router-dom";
import { PATHS, PLAYER_PARAM, asPlayer } from "~robin-hood/utils";
import type { Player } from "~robin-hood/utils";
import type {
  RegisteredStore,
  UnregisteredStore,
  Store,
  Equipo,
  Agent,
} from "~robin-hood/redux/store";
import { knowsPlayerSecret, getAgentPlayers } from "~robin-hood/redux/store";
import {
  useFirestoreDocData,
  useFirestore,
  //$FlowFixMe[missing-export]
  SuspenseWithPerf,
} from "reactfire";

type RegisteredProps = {
  store: RegisteredStore,
  updateStore: ($ReadOnly<Store>) => void,
};

export function RegisteredRobinHood(props: RegisteredProps): React$Node {
  return (
    <SuspenseWithPerf fallback={null} traceId={"load-registered"}>
      <RobinHoodWithFirebase {...props} />
    </SuspenseWithPerf>
  );
}

function RobinHoodWithFirebase(props: RegisteredProps) {
  const equipoRef = useFirestore()
    .collection("equipos")
    .doc(props.store.equipo.id);

  const equipo = useFirestoreDocData<Equipo>(equipoRef);
  return (
    <RegisteredRobinHoodClass
      {...props}
      equipo={equipo}
      equipoRef={equipoRef}
    />
  );
}

function RegisteredRobinHoodClass(props: {
  ...RegisteredProps,
  equipo: Equipo,
  equipoRef: FirestoreDocRef<Equipo>,
}): React$Node {
  const { equipo, equipoRef, store, updateStore } = props;
  function exitEquipo() {
    updateStore(
      ({ equipo: undefined, hasSeenInitialText: true }: UnregisteredStore)
    );
  }
  function updateEquipo(equipo: Equipo) {
    equipoRef.update(equipo).then(() => {
      updateStore({
        ...store,
        equipo: {
          id: store.equipo.id,
          ...equipo,
        },
      });
    });
  }
  let component;
  // User has not selected player, he has equipo but no player
  if (equipo.startTime == null) {
    component = (
      <WaitForStart
        updateEquipo={updateEquipo}
        equipo={store.equipo}
        agent={store.agent}
      />
    );
  } else {
    component = switchRegistered(
      store.agent,
      store,
      updateStore,
      equipo,
      updateEquipo
    );
  }
  return (
    <Router>
      <Frame store={store} updateStore={updateStore}>
        <Header />
        {component}
        <EquipoInfo store={store} exitEquipo={exitEquipo} />
        <Clock
          agent={store.agent}
          equipo={equipo}
          updateEquipo={updateEquipo}
          exitEquipo={exitEquipo}
        />
      </Frame>
    </Router>
  );
}

function switchRegistered(
  agent: Agent,
  store: RegisteredStore,
  updateLocalStore: (RegisteredStore) => void,
  equipo: Equipo,
  updateEquipo: (Equipo) => void
): React$Node {
  return (
    <Switch>
      <Route
        path={[
          PATHS.AGENTES(),
          PATHS.INICIAL(PLAYER_PARAM),
          PATHS.CONSOLA(PLAYER_PARAM),
          PATHS.DIGITOS(PLAYER_PARAM),
          PATHS.AHORCADO(PLAYER_PARAM),
          PATHS.POKER(PLAYER_PARAM),
          PATHS.CASILLAS(PLAYER_PARAM),
          PATHS.COLORES(PLAYER_PARAM),
          PATHS.SONIDOS(PLAYER_PARAM),
          PATHS.FINAL(PLAYER_PARAM),
        ]}
        render={({ match, history }) => {
          const players = getAgentPlayers(equipo, agent);
          const path = PATHS.FINAL(PLAYER_PARAM);
          const player = asPlayer(match.params.player);
          if (equipo.endTime != null && match.path !== path) {
            return <Redirect to={PATHS.FINAL(players[0])} />;
          } else if (equipo.endTime == null) {
            if (!player || !players.includes(player)) {
              return (
                <SeleccionAgentes
                  equipo={equipo}
                  history={history}
                  agent={agent}
                  store={store}
                  updateLocalStore={updateLocalStore}
                />
              );
            }
            if (!knowsPlayerSecret(store, player)) {
              return (
                <PruebaInicial
                  player={player}
                  agent={agent}
                  equipo={equipo}
                  store={store}
                  updateLocalStore={updateLocalStore}
                />
              );
            }
          }
          return inFrameRoute(
            player || players[0],
            store,
            updateLocalStore,
            equipo,
            updateEquipo,
            agent
          );
        }}
      />
      <Route
        render={({ match }) => {
          const player = asPlayer(match.params.player);
          if (player && equipo.endTime != null) {
            return <Redirect to={PATHS.FINAL(player)} />;
          }
          return <Redirect to={PATHS.AGENTES()} />;
        }}
      />
    </Switch>
  );
}

function inFrameRoute(
  player: Player,
  store: RegisteredStore,
  updateLocalStore: (RegisteredStore) => void,
  equipo: Equipo,
  updateEquipo: (Equipo) => void,
  agent: Agent
): React$Node {
  return (
    <InFrame>
      <Route path={PATHS.INICIAL(PLAYER_PARAM)}>
        <Redirect to={PATHS.CONSOLA(player)} />
      </Route>
      <Route path={PATHS.CONSOLA(PLAYER_PARAM)}>
        <PruebaConsola
          player={player}
          equipo={equipo}
          updateEquipo={updateEquipo}
          store={store}
          updateLocalStore={updateLocalStore}
        />
      </Route>
      <Route path={PATHS.DIGITOS(PLAYER_PARAM)}>
        <PruebaDigitos
          player={player}
          equipo={equipo}
          updateEquipo={updateEquipo}
          store={store}
          updateLocalStore={updateLocalStore}
        />
      </Route>
      <Route path={PATHS.AHORCADO(PLAYER_PARAM)}>
        <PruebaAhorcado
          player={player}
          equipo={equipo}
          updateEquipo={updateEquipo}
          store={store}
          updateLocalStore={updateLocalStore}
        />
      </Route>
      <Route path={PATHS.POKER(PLAYER_PARAM)}>
        <PruebaPoker
          player={player}
          equipo={equipo}
          updateEquipo={updateEquipo}
          store={store}
          updateLocalStore={updateLocalStore}
        />
      </Route>
      <Route path={PATHS.CASILLAS(PLAYER_PARAM)}>
        <PruebaCasillas
          player={player}
          store={store}
          updateLocalStore={updateLocalStore}
          equipo={equipo}
          updateEquipo={updateEquipo}
        />
      </Route>
      <Route path={PATHS.COLORES(PLAYER_PARAM)}>
        <PruebaColores
          equipo={equipo}
          updateEquipo={updateEquipo}
          player={player}
          store={store}
          updateLocalStore={updateLocalStore}
        />
      </Route>
      <Route path={PATHS.SONIDOS(PLAYER_PARAM)}>
        <PruebaSonidos
          equipo={equipo}
          updateEquipo={updateEquipo}
          player={player}
          store={store}
          updateLocalStore={updateLocalStore}
        />
      </Route>
      <Route path={PATHS.FINAL(PLAYER_PARAM)}>
        <PruebaFinal
          equipo={equipo}
          updateEquipo={updateEquipo}
          store={store}
          updateLocalStore={updateLocalStore}
        />
      </Route>
      <Toolbar store={store} player={player} equipo={equipo} agent={agent} />
    </InFrame>
  );
}
